/**
 * URLセマフォの実装
 * 複数のURLに対する並行アクセスを制御し、利用可能なURLの取得と解放を管理する
 * クールダウン機能付きで、特定のURLを一定時間利用不可にすることも可能
 */

/** キューに格納される要素の型 */
type QueueItem = {
  /** URLが利用可能になった際に呼び出される関数 */
  call: () => void
  /** タイムアウト用ID */
  timeoutId: ReturnType<typeof setTimeout>
}

/**
 * 複数のURLへのアクセスを制限するセマフォ
 * URLリソースを管理し、制限された並行処理を実現する
 */
export class UrlsSemaphore {
  /** 管理対象のURL配列 */
  #urls: readonly string[]
  /** 現在使用中のURLのインデックスを格納するセット */
  #usingIndexSet = new Set<number>()
  /** クールダウン中のURLのインデックスを格納するセット */
  #cooldownIndexSet = new Set<number>()
  /** URLの取得待ちキュー */
  #queue: QueueItem[] = []

  /**
   * URLセマフォを初期化する
   *
   * @param urls - 管理対象のURL配列
   */
  constructor(urls: readonly string[]) {
    this.#urls = urls
  }

  /**
   * 利用可能なURLを取得する
   * すべてのURLが使用中の場合は、指定時間待機してから利用可能になるか、タイムアウトエラーを返す
   *
   * @param timeout - 利用可能なURLを待つタイムアウト時間（ミリ秒）
   * @returns 利用可能なURL
   * @throws タイムアウトした場合にエラーをスロー
   */
  async acquire(timeout: number): Promise<string> {
    // 使用可能なURLがある場合はすぐに返す
    if (this.#usingIndexSet.size < this.#urls.length) {
      const index = this.#findAvailableIndex()
      const url = this.#getAndMarkUrl(index)
      return Promise.resolve(url)
    }

    // 全てのURLが使用中の場合はキューに入れて待機
    return new Promise<string>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        const index = this.#queue.findIndex(
          (item) => item.timeoutId === timeoutId,
        )
        if (index !== -1) {
          this.#queue.splice(index, 1)
          reject(new Error('Timeout while waiting for semaphore'))
        }
      }, timeout)

      this.#queue.push({
        call: () => {
          const index = this.#findAvailableIndex()
          const url = this.#getAndMarkUrl(index)
          resolve(url)
        },
        timeoutId,
      })
    })
  }

  /**
   * 使用中のURLを解放し、必要に応じてクールダウン期間を設ける
   * 待機中のキューがある場合、最初の要素を処理する
   *
   * @param url - 解放するURL
   * @param cooldown - オプションのクールダウン時間（ミリ秒）
   */
  async release(url: string, cooldown?: number): Promise<void> {
    const index = this.#urls.indexOf(url)

    // クールダウンが指定されている場合は処理
    if (cooldown !== undefined) {
      this.#usingIndexSet.add(index)
      this.#cooldownIndexSet.add(index)
      await new Promise((resolve) => setTimeout(resolve, cooldown))
      this.#cooldownIndexSet.delete(index)
    }

    // 使用中のセットから削除
    this.#usingIndexSet.delete(index)

    // 待機中のリクエストがある場合は処理
    const next = this.#queue.shift()
    if (next !== undefined) {
      const { call, timeoutId } = next
      clearTimeout(timeoutId)
      call()
    }
  }

  /**
   * すべてのURLがクールダウン中かどうかを確認
   *
   * @returns すべてのURLがクールダウン中の場合はtrue
   */
  isAllUrlsInCooldown(): boolean {
    return this.#cooldownIndexSet.size === this.#urls.length
  }

  /**
   * 使用可能なインデックスを見つける
   *
   * @returns 使用可能なURLのインデックス
   * @private
   */
  #findAvailableIndex(): number {
    return this.#urls.findIndex((_, index) => !this.#usingIndexSet.has(index))
  }

  /**
   * 指定したインデックスのURLを取得し、使用中としてマークする
   *
   * @param index - URL配列のインデックス
   * @returns 指定されたインデックスのURL
   * @private
   */
  #getAndMarkUrl(index: number): string {
    this.#usingIndexSet.add(index)
    return this.#urls[index]
  }
}
